/**
 * One shell for the whole site (18 Oct 2026): the top bar (app switcher, search on Task Force, light / dark, account
 * menu) and the rail on the left with every app — Task Force, Attendance, the Click Up report and, for admins,
 * Settings. An app someone does not have shows a lock; opening it explains and offers "Request access". Task Force,
 * the reports and Admin all sit in it, so the site looks and works as one system. Kept light (no Task Force data), so
 * the reports do not load Task Force to draw it.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { Access, AppKey } from '../lib/api';
import { useMe, useSignOut } from '../lib/session';
import { useThemeChoice } from '../lib/theme';
import { APP_VERSION, BUILT_AT } from '../lib/build';
import { Avatar, Icon } from '../taskforce/ui';
import { TfMark } from '../taskforce/brand';
import { TypeGlyph } from '../taskforce/typeicons';
import { Celebration, celebrationsOn, setCelebrations } from '../taskforce/celebrate';
import '../styles/taskforce.css';
import '../styles/unified.css';

export type Area = AppKey | 'settings';

interface Item {
  key: Area;
  label: string;
  short: string;
  icon: string;
  path: string;
  allowed: boolean;
}

/** Settings: one area for the whole site, opening on what this person manages. */
export function settingsPath(_a?: Access): string {
  return '/settings';
}

/** The apps, in rail order, with whether this person can open each. */
export function useApps(): Item[] {
  const a = useMe().data?.access;
  const items: Item[] = [
    {
      key: 'taskforce',
      label: 'Task Force',
      short: 'Task Force',
      icon: 'home',
      path: '/taskforce',
      allowed: !!a?.apps.taskforce,
    },
    {
      key: 'attendance',
      label: 'Attendance',
      short: 'Attendance',
      icon: 'clock',
      path: '/attendance',
      allowed: !!a?.apps.attendance,
    },
    {
      key: 'clickup',
      label: 'Click Up report',
      short: 'Click Up',
      icon: 'chart',
      path: '/clickup',
      allowed: !!a?.apps.clickup,
    },
  ];
  if (a?.settings)
    items.push({
      key: 'settings',
      label: 'Settings',
      short: 'Settings',
      icon: 'gear',
      path: settingsPath(a),
      allowed: true,
    });
  return items;
}

/** A menu that closes on a click outside or Esc. */
function useDismiss(open: boolean, close: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && close();
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && close();
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open, close]);
  return ref;
}

function MenuItem({
  icon,
  children,
  onClick,
  locked,
  current,
}: {
  icon: string;
  children: ReactNode;
  onClick: () => void;
  locked?: boolean;
  current?: boolean;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      className={'tfmenu-item' + (current ? ' on' : '')}
      onClick={onClick}
      aria-current={current ? 'page' : undefined}
    >
      <Icon n={icon} /> <span className="tfgrow">{children}</span>
      {locked && (
        <span className="tflocked" title="You don’t have access" aria-label="(locked)">
          <Icon n="lock" size={13} />
        </span>
      )}
    </button>
  );
}

/**
 * Light or dark, beside the avatar in every app's top bar (27 Oct 2026; it was in the account menu): a moon in light
 * mode, a sun in dark. Both icons are drawn and cross-fade with a turn; with less motion asked for, they only fade.
 */
function ThemeButton() {
  const [theme, flip] = useThemeChoice();
  const label = theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode';
  return (
    <button type="button" className="tftheme" data-mode={theme} onClick={flip} aria-label={label} title={label}>
      <span className="tftheme-i tftheme-moon">
        <Icon n="moon" size={16} />
      </span>
      <span className="tftheme-i tftheme-sun">
        <Icon n="sun" size={16} />
      </span>
    </button>
  );
}

export function AppTopBar({
  area,
  onSearch,
  live = 'live',
}: {
  area: Area;
  /** Task Force's search (the command bar); the reports have none. */
  onSearch?: () => void;
  live?: 'live' | 'reconnecting';
}) {
  const me = useMe();
  const navigate = useNavigate();
  const signOut = useSignOut();
  const apps = useApps();
  const [party, setParty] = useState(celebrationsOn);
  const [ws, setWs] = useState(false);
  const [acct, setAcct] = useState(false);
  // The phone's You tab opens this menu (taskforce/shell.tsx, ACCOUNT_EVENT).
  useEffect(() => {
    const open = () => setAcct(true);
    window.addEventListener('tf-account', open);
    return () => window.removeEventListener('tf-account', open);
  }, []);
  const wsRef = useDismiss(
    ws,
    useCallback(() => setWs(false), []),
  );
  const acctRef = useDismiss(
    acct,
    useCallback(() => setAcct(false), []),
  );
  const name = me.data?.displayName ?? '';
  const here = apps.find((x) => x.key === area);
  const mac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
  const viewing = !!me.data?.impersonatedBy;
  return (
    <header className="tftop">
      <Celebration firstName={name.split(' ')[0] || undefined} />
      <div className="tfwrap" ref={wsRef}>
        <button
          type="button"
          className="tfws"
          onClick={() => setWs(!ws)}
          aria-haspopup="menu"
          aria-expanded={ws}
          aria-label="Workspace menu"
        >
          <TfMark size={18} />
          <span className="tfws-name">{here?.label ?? 'Task Force'}</span>
          <Icon n="chevDown" size={12} />
        </button>
        {ws && (
          <div className="tfpop tfws-pop" role="menu" aria-label="Apps">
            <div className="tfmenu-h">
              <b>dubizzle labs</b>
              <span className="tfmuted">Switch app</span>
            </div>
            {apps.map((x) => (
              <MenuItem
                key={x.key}
                icon={x.icon}
                locked={!x.allowed}
                current={x.key === area}
                // A locked app says so (with Request access); other links to it just go home (App.tsx).
                onClick={() => (setWs(false), navigate(x.path, x.allowed ? undefined : { state: { ask: true } }))}
              >
                {x.label}
              </MenuItem>
            ))}
          </div>
        )}
      </div>
      {onSearch ? (
        <button type="button" className="tfsearchbtn" onClick={onSearch} aria-label="Search" aria-keyshortcuts="Meta+K">
          <Icon n="search" size={14} />
          <span>Search</span>
          <kbd>{mac ? '⌘K' : 'Ctrl K'}</kbd>
        </button>
      ) : (
        <span />
      )}
      <div className="tftop-r tfwrap" ref={acctRef}>
        {live === 'reconnecting' && (
          <span className="tflive-off" role="status" title="Changes by others will show once the connection is back.">
            <span className="tfspin" /> Reconnecting…
          </span>
        )}
        <ThemeButton />
        <button
          type="button"
          className="tfme"
          onClick={() => setAcct(!acct)}
          aria-label="Account"
          aria-haspopup="menu"
          aria-expanded={acct}
        >
          <Avatar name={name || '?'} size={28} />
        </button>
        {acct && (
          <div className="tfpop tfmenu-pop" role="menu">
            <div className="tfmenu-h">
              <b>{name}</b>
              <span className="tfmuted">{me.data?.email}</span>
            </div>
            {/* On a phone the feedback button is here rather than over the page (taskforce/trial.tsx). */}
            {window.location.pathname.startsWith('/taskforce') && (
              <span className="tfme-feedback">
                <MenuItem
                  icon="megaphone"
                  onClick={() => (setAcct(false), window.dispatchEvent(new Event('tf-feedback')))}
                >
                  Send feedback
                </MenuItem>
              </span>
            )}
            {/* Everyone with Task Force can connect Claude or another assistant (29 Oct 2026). */}
            {me.data?.access?.apps.taskforce && (
              <MenuItem icon="sparkle" onClick={() => (setAcct(false), navigate('/taskforce/-/ai'))}>
                Connect an AI assistant
              </MenuItem>
            )}
            <MenuItem
              icon="check"
              onClick={() => {
                setCelebrations(!party);
                setParty(!party);
              }}
            >
              {party ? 'Turn off go-live celebrations' : 'Turn on go-live celebrations'}
            </MenuItem>
            {/* A view-as tab never signs the admin out; it ends from the banner. */}
            {!viewing && (
              <MenuItem
                icon="logout"
                onClick={async () => {
                  await signOut();
                  navigate('/sign-in', { replace: true });
                }}
              >
                Log out
              </MenuItem>
            )}
            <p className="tfmenu-ver">
              v{APP_VERSION} · built {BUILT_AT}
            </p>
          </div>
        )}
      </div>
    </header>
  );
}

/** The rail: every app, locked where someone does not have it. */
export function AppRail({ area }: { area: Area }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const apps = useApps();
  // Releases and the TaskForce Report sit under Task Force here (3 Nov 2026, people's feedback; on a phone, where
  // there is no rail, they stay in the sidebar).
  const sub = pathname.startsWith('/taskforce/-/releases')
    ? 'releases'
    : pathname.startsWith('/taskforce-report')
      ? 'report'
      : null;
  const subItem = (key: string, label: string, short: string, icon: ReactNode, path: string) => (
    <button
      key={key}
      type="button"
      className={'tfrail-i tfrail-sub' + (sub === key ? ' on' : '')}
      onClick={() => navigate(path)}
      aria-current={sub === key ? 'page' : undefined}
      aria-label={label}
      title={label}
    >
      <span>{icon}</span>
      <em>{short}</em>
    </button>
  );
  return (
    <nav className="tfrail" aria-label="Apps">
      {apps.map((x) => [
        <button
          key={x.key}
          type="button"
          className={'tfrail-i' + (x.key === area && !sub ? ' on' : '') + (x.allowed ? '' : ' locked')}
          onClick={() => navigate(x.path, x.allowed ? undefined : { state: { ask: true } })}
          aria-current={x.key === area && !sub ? 'page' : undefined}
          aria-label={x.allowed ? x.label : `${x.label} (locked)`}
          title={x.allowed ? x.label : `${x.label}: you don’t have access`}
        >
          <span>
            <Icon n={x.icon} size={18} />
            {!x.allowed && (
              <i className="tfrail-lock" aria-hidden="true">
                <Icon n="lock" size={10} />
              </i>
            )}
          </span>
          <em>{x.short}</em>
        </button>,
        x.key === 'taskforce' &&
          x.allowed && [
            subItem('releases', 'Releases', 'Releases', <TypeGlyph icon="rocket" size={16} />, '/taskforce/-/releases'),
            subItem('report', 'TaskForce Report', 'Report', <Icon n="chart" size={17} />, '/taskforce-report'),
          ],
      ])}
    </nav>
  );
}

/** The tab title for the area. */
function useTitle(area: Area) {
  const label = useApps().find((x) => x.key === area)?.label ?? 'dubizzle labs';
  useEffect(() => {
    const before = document.title;
    document.title = `${label} · dubizzle labs`;
    return () => {
      document.title = before;
    };
  }, [label]);
}

/**
 * Marks the page root while the shell is on screen, so unified.css can give the reports Task Force's tokens. A class,
 * not `:root:has(.appshell)`: that made the browser re-check the whole page's styles whenever anything was added.
 * Counted, because one shell can mount before the last one unmounts when moving between pages.
 */
let shells = 0;
function useShellMark() {
  useLayoutEffect(() => {
    if (shells++ === 0) document.documentElement.classList.add('inshell');
    return () => {
      if (--shells === 0) document.documentElement.classList.remove('inshell');
    };
  }, []);
}

/** The reports and Admin inside the shell. */
export function AppShell({ area, children }: { area: Area; children: ReactNode }) {
  useTitle(area);
  useShellMark();
  return (
    <div className="tfapp appshell">
      <AppTopBar area={area} />
      <div className="tfframe">
        <AppRail area={area} />
        <div className="tfshell">
          <main className="tfmain appmain">{children}</main>
        </div>
      </div>
    </div>
  );
}
